import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConnectionProfileCreate, ConnectionProfileResponse, ConnectionProfileUpdate } from '@flixflow/shared';
import {
  listProfiles,
  createProfile,
  updateProfile,
  deleteProfile,
  testProfile,
  type ProbeResult,
} from '../api/connectionProfiles.js';
import { ConnectionProfileForm } from '../components/ConnectionProfileForm.js';

type ModalState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; profile: ConnectionProfileResponse };

export function ConnectionProfilesPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<ModalState>({ mode: 'closed' });
  const [testResults, setTestResults] = useState<Record<string, ProbeResult>>({});
  const [testingId, setTestingId] = useState<string | null>(null);

  const { data: profiles = [], isLoading, error } = useQuery({
    queryKey: ['connection-profiles'],
    queryFn: listProfiles,
  });

  const createMut = useMutation({
    mutationFn: (data: ConnectionProfileCreate) => createProfile(data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['connection-profiles'] }); setModal({ mode: 'closed' }); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConnectionProfileUpdate }) => updateProfile(id, data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['connection-profiles'] }); setModal({ mode: 'closed' }); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteProfile,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connection-profiles'] }),
  });

  async function handleTest(id: string) {
    setTestingId(id);
    try {
      const result = await testProfile(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch {
      setTestResults((prev) => ({ ...prev, [id]: { ok: false, durationMs: 0, error: 'Anfrage fehlgeschlagen' } }));
    } finally {
      setTestingId(null);
    }
  }

  async function handleFormSubmit(data: ConnectionProfileCreate | ConnectionProfileUpdate) {
    if (modal.mode === 'create') {
      await createMut.mutateAsync(data as ConnectionProfileCreate);
    } else if (modal.mode === 'edit') {
      await updateMut.mutateAsync({ id: modal.profile.id, data: data as ConnectionProfileUpdate });
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Verbindungsprofile</h2>
        <button
          onClick={() => setModal({ mode: 'create' })}
          className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
        >
          Neu
        </button>
      </div>

      {isLoading && <p className="text-sm text-neutral-500">Laden …</p>}
      {error && <p className="text-sm text-red-600">Fehler beim Laden der Profile.</p>}

      {!isLoading && profiles.length === 0 && (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
          Keine Verbindungsprofile vorhanden. Erstellen Sie ein Profil, um zu beginnen.
        </div>
      )}

      {profiles.length > 0 && (
        <div className="rounded-lg border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Name</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Host</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Datenbank</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Benutzer</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {profiles.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{p.host}:{p.port}</td>
                  <td className="px-4 py-3 text-neutral-600">{p.database}</td>
                  <td className="px-4 py-3 text-neutral-600">{p.username}</td>
                  <td className="px-4 py-3">
                    <TestBadge result={testResults[p.id]} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => void handleTest(p.id)}
                        disabled={testingId === p.id}
                        className="text-xs px-2.5 py-1 rounded border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50"
                      >
                        {testingId === p.id ? 'Teste …' : 'Testen'}
                      </button>
                      <button
                        onClick={() => setModal({ mode: 'edit', profile: p })}
                        className="text-xs px-2.5 py-1 rounded border border-neutral-300 hover:bg-neutral-100"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Profil "${p.name}" wirklich löschen?`)) {
                            deleteMut.mutate(p.id);
                          }
                        }}
                        className="text-xs px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.mode !== 'closed' && (
        <Modal
          title={modal.mode === 'create' ? 'Neues Verbindungsprofil' : 'Profil bearbeiten'}
          onClose={() => setModal({ mode: 'closed' })}
        >
          <ConnectionProfileForm
            initial={modal.mode === 'edit' ? modal.profile : undefined}
            onSubmit={handleFormSubmit}
            onCancel={() => setModal({ mode: 'closed' })}
          />
          {(createMut.error ?? updateMut.error) && (
            <p className="mt-3 text-sm text-red-600">
              {String((createMut.error ?? updateMut.error)?.message)}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

function TestBadge({ result }: { result?: ProbeResult }) {
  if (!result) return <span className="text-neutral-400">–</span>;
  if (result.ok) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
        OK · {result.durationMs} ms
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col gap-0.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
      <span>Fehler · {result.durationMs} ms</span>
      {result.error && <span className="text-red-500 max-w-xs break-words">{result.error}</span>}
    </span>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">
            ×
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
